import React from "react"
import { useTranslation } from "react-i18next"

import { Bike, ErrorResponse } from "models"
import {
	bikesNotifications,
	compareIntersectionEgalityOf2Objects,
	NOTIFICATION_BIKE_BUYBACK_VALIDATION,
	NOTIFICATION_BIKE_RETURN_VALIDATION,
	NOTIFICATION_PURCHASE_VALIDATION,
	PRIVATE_GENDER_VALUE,
} from "utils"

import * as BikeApi from "features/bike/api/BikeApi"

import { Button, Modal, Spinner } from "components/Elements"
import { FormElement, formStatusEnum } from "components/Form"
import { useAlert, useForm, useUser } from "hooks"

type NotificationActionsProps = {
	bike: Bike
	refreshBikes: () => Promise<void>
}

const NOTIFICATION_ACCEPTED = "NOTIFICATION_ACCEPTED"
const NOTIFICATION_REFUSED = "NOTIFICATION_REFUSED"

const FNUCI_PRIVATE_FIELDS_LIST = ["gender", "last_name", "first_name", "phone", "mail"]
const FNUCI_COMPANY_FIELDS_LIST = ["gender", "social_reason", "phone", "mail"]

type ModalInformationProps = {
	userChoice: string
	title: string
	description: string
	buttonLabel: string
}

const defaultModalInformation: ModalInformationProps = {
	userChoice: null,
	title: null,
	description: null,
	buttonLabel: null,
}

export const NotificationActions: React.FC<NotificationActionsProps> = ({ bike, refreshBikes }) => {
	const { formStatus, setFormStatus } = useForm({
		defaultFormStatus: formStatusEnum.ready,
	})
	const [isModalOpen, setModalIsOpen] = React.useState<boolean>(false)
	const [modalInformation, setModalInformation] =
		React.useState<ModalInformationProps>(defaultModalInformation)

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { user } = useUser()

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const isPurchaseActionNeeded = bike.bike_action_needed_code === NOTIFICATION_PURCHASE_VALIDATION
	const isBackStoreOrBuyActionNeeded =
		bike.bike_action_needed_code === NOTIFICATION_BIKE_RETURN_VALIDATION ||
		bike.bike_action_needed_code === NOTIFICATION_BIKE_BUYBACK_VALIDATION

	const openModal = (bike: Bike, userChoice: string) => {
		// We set content in modal with the user's action (accepted or refused) AND the type of notification
		const bikeStatusNotification = bikesNotifications.find(
			(notification) => notification.id === bike.bike_action_needed_code
		)
		setModalIsOpen(true)

		const isAccepted = userChoice === NOTIFICATION_ACCEPTED

		setModalInformation((prevModalInformation: ModalInformationProps) => ({
			...prevModalInformation,
			userChoice,
			title: bikeStatusNotification?.modal.title,
			description: isAccepted
				? bikeStatusNotification?.modal.acceptedDescription
				: bikeStatusNotification?.modal.refusedDescription,
			buttonLabel: isAccepted
				? bikeStatusNotification?.modal.acceptedButtonLabel
				: bikeStatusNotification?.modal.refusedButtonLabel,
		}))
	}

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()

		try {
			// prevent form to be submitted again
			setFormStatus(formStatusEnum.loading)

			const updatedNotification = {
				bike_id: bike.bike_id,
				bike_action_needed_code: bike.bike_action_needed_code,
				value: modalInformation.userChoice === NOTIFICATION_ACCEPTED,
			}

			const { res: confirmUpdateBikeNotification, getError } =
				await BikeApi.updateBikeNotification(updatedNotification)

			if (getError()) throw getError()

			// We need the new row_version sent in the response
			// It will be send to API if we have to sync the FNUCI
			if (!confirmUpdateBikeNotification?.row_version) throw getError()

			// The owner should be checked/updated if 1 condition is met among:
			// - in the purchase case, if the bike is `accepted`
			// - in the the back store case, if the bike is `refused` (the bike is then kept but owner's info could have changed during the bike transfer request)
			// ------------------------
			if (
				(modalInformation.userChoice === NOTIFICATION_ACCEPTED && isPurchaseActionNeeded) ||
				(modalInformation.userChoice === NOTIFICATION_REFUSED &&
					isBackStoreOrBuyActionNeeded)
			) {
				// We compare user.description and bike.bike_owner on the 5 important fields
				const isBikeOwnerDifferent = compareIntersectionEgalityOf2Objects(
					user.description,
					bike.bike_owner,
					user.description.gender === PRIVATE_GENDER_VALUE
						? FNUCI_PRIVATE_FIELDS_LIST
						: FNUCI_COMPANY_FIELDS_LIST
				)

				if (isBikeOwnerDifferent) {
					// If differences, we update the fnuci with the bike owner information
					const bikeOwner = {
						gender: user.description.gender,
						phone: user.description.phone,
						mail: user.description.mail,
						...(() => {
							if (user.description.gender === PRIVATE_GENDER_VALUE) {
								return {
									first_name: user.description.first_name,
									last_name: user.description.last_name,
								}
							} else {
								return {
									social_reason: user.description.social_reason,
								}
							}
						})(),
					}

					const updatedBikePayload = {
						bike_id: bike.bike_id,
						row_version: confirmUpdateBikeNotification.row_version,
						bike_owner: bikeOwner,
					}

					const { getError: getUpdateBikeError } = await BikeApi.updateBike(
						updatedBikePayload
					)
					if (getUpdateBikeError()) throw getUpdateBikeError()
				}
			}

			alert("success", t("info_update", { ns: "form" }))
			setModalIsOpen(false)
			await refreshBikes()
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.ready)
		}
	}

	return (
		<>
			<div className="flex justify-center pt-6">
				<Button
					onClick={() => openModal(bike, NOTIFICATION_ACCEPTED)}
					className="mx-1 md:mx-4 py-1 px-5"
					variant="secondary"
					size="small"
				>
					{t("global.accept")}
				</Button>
				<Button
					onClick={() => openModal(bike, NOTIFICATION_REFUSED)}
					className="mx-1 md:mx-4 py-1 px-5"
					variant="secondaryBordered"
					size="small"
				>
					{t("global.refuse")}
				</Button>
			</div>

			<Modal
				title={modalInformation.title}
				isOpen={isModalOpen}
				setIsOpen={setModalIsOpen}
				className={"max-w-xl"}
			>
				<p className="text-center px-6 mt-8">{modalInformation.description}</p>

				<form>
					<FormElement
						value={bike.bike_id}
						name="bikeId"
						type="text"
						id="bikeId"
						className="mb-8 md:mb-12 md:w-1/2 mx-auto"
						disabled={true}
						forceFloating={true}
					/>

					{isReady && (
						<div className="flex justify-center">
							<Button
								onClick={handleSubmit}
								className="mx-1 md:mx-2 px-5 md:px-12 py-1"
								variant="secondary"
							>
								{modalInformation.buttonLabel}
							</Button>
							<Button
								onClick={() => setModalIsOpen(false)}
								className="mx-1 md:mx-2 px-8 py-1 min-w-0"
								variant="secondaryBordered"
							>
								{t("global.cancel")}
							</Button>
						</div>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}
				</form>
			</Modal>
		</>
	)
}
